'use client'

import Link from 'next/link'
import { useLandingPageViewmodel } from '@/features/landing/landing-page.hooks'
import { LandingHomeLink } from '@/features/landing/view/LandingHomeLink'
import { LandingLeadForm } from '@/features/landing/view/LandingLeadForm'
import { routePaths } from '@/shared/config/site'
import { Button } from '@/shared/ui/Button'

export function VibengineersLandingPage() {
  const landingPageViewmodel = useLandingPageViewmodel()
  const { content } = landingPageViewmodel.page

  return (
    <div className="page-shell bg-[#020706] text-phosphor-ivory">
      <main className="relative overflow-hidden" id={'content'}>
        <div className="absolute left-5 top-5 z-20 md:left-8 md:top-8">
          <LandingHomeLink className="hover:bg-[#062017]" />
        </div>
        <section className="relative overflow-hidden px-5 pb-8 pt-20 md:px-8 md:pt-20 lg:px-10">
          <img
            alt={content.visual.alt}
            className="absolute inset-y-0 right-[-54rem] h-full max-w-none object-cover opacity-82 md:right-[-34rem] lg:right-[-18rem] xl:right-[-8rem]"
            decoding="async"
            height={855}
            src={content.visual.src}
            width={1800}
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_76%_50%,rgba(0,255,159,0.16),transparent_38%),linear-gradient(90deg,#020706_0%,#020706_34%,rgba(2,7,6,0.58)_62%,#020706_100%)]" />
          <div className="relative mx-auto flex max-w-7xl flex-col justify-start">
            <div className="max-w-5xl">
              <div className="mb-7 hidden max-w-3xl flex-wrap gap-2 font-mono text-xs font-semibold text-[#00ff9f] md:flex">
                {content.proofItems.map((item) => (
                  <span key={item.title} className="bg-[#020706]/76 px-3 py-2">
                    {item.title}
                  </span>
                ))}
              </div>
              <h1 className="max-w-5xl font-sans text-4xl font-black leading-none text-[#d9fff3] md:text-6xl lg:text-7xl">
                {content.title}
              </h1>
              <p className="mt-7 max-w-3xl text-lg font-semibold leading-8 text-white/78 md:text-2xl md:leading-10">
                {content.intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="border-[#00ff9f] bg-[#00ff9f] text-[#020706] hover:bg-[#18ef9f]" size="lg">
                  <a href="#lead">{content.primaryCta}</a>
                </Button>
                <Button asChild className="border-[#00ff9f]/24 bg-[#06130f] text-white hover:bg-[#082419]" size="lg">
                  <Link href={routePaths.service}>{content.secondaryCta}</Link>
                </Button>
              </div>
            </div>
            <div className="mt-12 flex max-w-5xl flex-wrap items-start gap-3">
              {content.processItems.map((item, index) => (
                <article
                  key={item}
                  className={`min-w-60 flex-1 bg-[#020706]/82 p-4 ${index === 1 ? 'lg:mt-9' : ''} ${
                    index === 2 ? 'lg:mt-4' : ''
                  }`}
                >
                  <p className="font-mono text-xs font-semibold text-[#00ff9f]">CHECKPOINT {index + 1}</p>
                  <p className="mt-3 text-sm leading-7 text-white/66">{item}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section className="mx-auto flex max-w-7xl flex-col gap-6 px-5 pb-10 pt-4 md:px-8 lg:flex-row lg:items-start lg:px-10">
          <div className="grid gap-6 lg:flex-1">
            <div className="flex flex-wrap items-start gap-3">
              {content.proofItems.map((item, index) => (
                <article
                  key={item.title}
                  className={`min-w-64 flex-1 bg-[#04110d] p-5 ${index === 1 ? 'lg:mt-8' : ''} ${
                    index === 2 ? 'lg:mt-3' : ''
                  }`}
                >
                  <h2 className="font-mono text-base font-semibold text-[#00ff9f]">{item.title}</h2>
                  <p className="mt-4 text-sm leading-7 text-white/68">{item.description}</p>
                </article>
              ))}
            </div>
            <div className="flex flex-col gap-4 md:flex-row">
              <SignalList items={content.fitItems} title={content.fitTitle} />
              <SignalList warning items={content.boundaryItems} title={content.boundaryTitle} />
            </div>
          </div>
          <LandingLeadForm className="border-[#00ff9f]/34 bg-[#03100c] lg:sticky lg:top-8 lg:w-[30rem]" />
        </section>
        <section className="mx-auto grid max-w-7xl gap-5 px-5 pb-16 md:px-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:px-10">
          <p className="max-w-4xl text-3xl font-black leading-10 text-[#00ff9f] md:text-5xl md:leading-none">
            {content.closingTitle}
          </p>
          <Button asChild className="border-[#00ff9f] bg-[#00ff9f] text-[#020706] hover:bg-[#18ef9f]" size="lg">
            <a href="#lead">{content.primaryCta}</a>
          </Button>
        </section>
      </main>
    </div>
  )
}

function SignalList({ items, title, warning = false }: { items: string[]; title: string; warning?: boolean }) {
  return (
    <article className={warning ? 'flex-1 bg-[#18080b] p-5' : 'flex-1 border-l-4 border-[#00ff9f] bg-[#04110d] p-5'}>
      <h2 className={warning ? 'text-xl font-black text-[#ff3d5a]' : 'text-xl font-black text-[#d9fff3]'}>{title}</h2>
      <ul className="mt-4 space-y-3">
        {items.map((item) => (
          <li key={item} className="font-mono text-sm leading-7 text-white/64">
            {item}
          </li>
        ))}
      </ul>
    </article>
  )
}
